import { Badge, Tab, Tabs } from '@mui/material';
import { t } from 'i18next';
import { Link, matchPath, useLocation, useParams } from 'react-router-dom';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { InvoiceMissionDataList } from 'src/shared/models/invoiceMission/invoiceMission';
import { Error } from '@mui/icons-material';
import { convertSendingTypeIntToString } from './invoiceMissionSendingTypeConverter';

type InvoiceTab = {
  id: number;
  label: string;
};

const tabs: InvoiceTab[] = [
  {
    id: InvoiceSendingTypeIntegerEnum.NotSet,
    label: t('invoice:invoiceMissionDetailTabs.all'),
  },
  {
    id: InvoiceSendingTypeIntegerEnum.Main,
    label: t('invoice:invoiceMissionDetailTabs.mainSending'),
  },
  {
    id: InvoiceSendingTypeIntegerEnum.Reminder1,
    label: t('invoice:invoiceMissionDetailTabs.reminderOne'),
  },
  {
    id: InvoiceSendingTypeIntegerEnum.Reminder2,
    label: t('invoice:invoiceMissionDetailTabs.reminderTwo'),
  },
];

const useRouteMatch = (patterns: readonly string[]) => {
  const { pathname } = useLocation();
  for (let i = 0; i < patterns.length; i += 1) {
    const possibleMatch = matchPath(patterns[i], pathname);
    if (possibleMatch) {
      return possibleMatch;
    }
  }
  return null;
};

type InvoiceMissionDetailTabsProps = {
  dataList: InvoiceMissionDataList[];
};

export const InvoiceMissionDetailTabs = (props: InvoiceMissionDetailTabsProps) => {
  const { dataList } = props;

  const needsApproval = (sendingId: number) => {
    const st = convertSendingTypeIntToString(sendingId);
    if (!st) return false;
    const data = dataList.find((x) => x.sendingType === st);
    return Boolean(data?.approval.approvalRequired && !data.approval.approvedAt && !data.approval.deniedAt);
  };

  const { id } = useParams<{ id: string }>();
  const routeMatch = useRouteMatch(tabs.map((x) => `${RoutesEnum.InvoiceMissionDetailSendingType}/${id}/${x.id}`));
  const currentTab = routeMatch?.pattern?.path;

  return (
    <Tabs
      value={currentTab}
      allowScrollButtonsMobile
      variant='scrollable'
      scrollButtons='auto'
      textColor='primary'
      sx={{
        '& button': {
          textTransform: 'uppercase',
        },
        '& .hasBadge': {
          pr: '2rem',
          '& .MuiBadge-badge': {
            right: '-10px',
            top: '-2px',
          },
        },
      }}
    >
      {tabs.map((tab: InvoiceTab) => {
        const showDot = needsApproval(tab.id);
        return (
          <Tab
            label={
              showDot ? (
                <Badge
                  sx={{
                    '& .MuiBadge-badge': {
                      top: -0,
                      right: -8,
                    },
                  }}
                  badgeContent={<Error fontSize='small' color='error' />}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <span>{tab.label}</span>
                </Badge>
              ) : (
                tab.label
              )
            }
            key={tab.id}
            value={`${RoutesEnum.InvoiceMissionDetailSendingType}/${id}/${tab.id}`}
            to={`${RoutesEnum.InvoiceMissionDetailSendingType}/${id}/${tab.id}`}
            component={Link}
          />
        );
      })}
    </Tabs>
  );
};
